import React, { useState, useEffect, useRef } from 'react';
import { Activity, AlertCircle, Zap, ChevronUp } from 'lucide-react';

/**
 * Context Window Indicator Component
 * Shows current context usage as a percentage with color-coded warnings
 * Click to open dropdown with detailed breakdown and "Compact Now" option
 */
const ContextWindowIndicator = ({
  conversation,
  selectedModel,
  use1MContext = false,
  onCompact
}) => {
  const [contextData, setContextData] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [compacting, setCompacting] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // Fetch context usage data
  useEffect(() => {
    if (!conversation?.id || !selectedModel) return;

    const fetchContextData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`/api/conversations/${conversation.id}/context-usage?model=${selectedModel}&use1MContext=${use1MContext}`);

        if (!response.ok) {
          throw new Error('Failed to fetch context usage');
        }

        const data = await response.json();
        setContextData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching context usage:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchContextData();

    // Refresh every 5 seconds when dropdown is open
    const interval = showDropdown ? setInterval(fetchContextData, 5000) : null;

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [conversation?.id, selectedModel, use1MContext, showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showDropdown]);

  const handleCompact = async () => {
    if (!conversation?.id) return;

    try {
      setCompacting(true);

      const response = await fetch(`/api/conversations/${conversation.id}/compact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: selectedModel })
      });

      if (!response.ok) {
        throw new Error('Failed to compact context');
      }

      const result = await response.json();

      // Refresh context data
      const updatedResponse = await fetch(`/api/conversations/${conversation.id}/context-usage?model=${selectedModel}&use1MContext=${use1MContext}`);
      const updatedData = await updatedResponse.json();
      setContextData(updatedData);

      // Notify parent component
      if (onCompact) {
        onCompact(result);
      }

      // Show success briefly
      setTimeout(() => {
        setShowDropdown(false);
      }, 2000);
    } catch (error) {
      console.error('Error compacting context:', error);
      alert('Failed to compact context. Please try again.');
    } finally {
      setCompacting(false);
    }
  };

  // Show error state
  if (error) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-600"
        title={`Error: ${error}`}
      >
        <AlertCircle className="w-4 h-4" />
        <span className="text-sm font-semibold">!</span>
      </div>
    );
  }

  // Show loading state or placeholder
  if (loading && !contextData) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-200 bg-gray-50 text-gray-400">
        <Activity className="w-4 h-4 animate-pulse" />
        <span className="text-sm font-semibold">...</span>
      </div>
    );
  }

  if (!contextData) {
    return null;
  }

  const {
    currentTokens,
    tokenLimit,
    percentage,
    breakdown,
    canCompact,
    messagesCount
  } = contextData;

  // Determine color based on usage percentage
  const getIndicatorColor = () => {
    if (percentage >= 90) return 'text-red-600 bg-red-50 border-red-300';
    if (percentage >= 70) return 'text-orange-600 bg-orange-50 border-orange-300';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-50 border-yellow-300';
    return 'text-blue-600 bg-blue-50 border-blue-300';
  };

  const getProgressColor = () => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-orange-500';
    if (percentage >= 50) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const getIcon = () => {
    if (percentage >= 90) return <AlertCircle className="w-4 h-4" />;
    if (percentage >= 70) return <Activity className="w-4 h-4" />;
    return <Activity className="w-4 h-4" />;
  };

  const formatNumber = (num) => {
    return num.toLocaleString();
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Indicator Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all ${getIndicatorColor()} hover:shadow-md`}
        title={`Context: ${percentage}% used (${formatNumber(currentTokens)} / ${formatNumber(tokenLimit)} tokens)`}
      >
        {getIcon()}
        <span className="text-sm font-semibold">{Math.round(percentage)}%</span>
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute bottom-full right-0 mb-2 w-80 bg-white border border-gray-300 rounded-lg shadow-xl z-50">
          <div className="p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Context Window</h3>
              <span className={`text-xs font-medium px-2 py-1 rounded ${getIndicatorColor()}`}>
                {Math.round(percentage)}% used
              </span>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-xs text-gray-600 mb-1">
                <span>{formatNumber(currentTokens)} tokens</span>
                <span>{formatNumber(tokenLimit)} limit</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${getProgressColor()}`}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                />
              </div>
            </div>

            {/* Breakdown */}
            <div className="space-y-2 mb-4 text-xs">
              <div className="flex justify-between text-gray-700">
                <span>System prompt:</span>
                <span className="font-medium">{formatNumber(breakdown.systemPrompt)} tokens</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Messages ({messagesCount}):</span>
                <span className="font-medium">{formatNumber(breakdown.messages)} tokens</span>
              </div>
              {breakdown.images > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Images:</span>
                  <span className="font-medium">{formatNumber(breakdown.images)} tokens</span>
                </div>
              )}
              {breakdown.tools > 0 && (
                <div className="flex justify-between text-gray-700">
                  <span>Tool calls:</span>
                  <span className="font-medium">{formatNumber(breakdown.tools)} tokens</span>
                </div>
              )}
            </div>

            {/* Warning Messages */}
            {percentage >= 90 && (
              <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
                <strong>Critical:</strong> Context nearly full. Compact now to avoid errors.
              </div>
            )}
            {percentage >= 70 && percentage < 90 && (
              <div className="mb-4 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                <strong>Warning:</strong> Context usage is high. Consider compacting soon.
              </div>
            )}

            {/* Compact Button */}
            {canCompact && (
              <button
                onClick={handleCompact}
                disabled={compacting}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  compacting
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                <Zap className="w-4 h-4" />
                {compacting ? 'Compacting...' : 'Compact Now'}
              </button>
            )}

            {!canCompact && (
              <div className="text-xs text-gray-500 text-center py-2">
                Not enough messages to compact (need 5+)
              </div>
            )}

            {/* Info */}
            <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
              <p>Compacting summarizes older messages to free up space while preserving context.</p>
            </div>
          </div>

          {/* Arrow indicator */}
          <div className="absolute top-full right-6 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-gray-300"
               style={{ marginTop: '-1px' }}
          />
          <div className="absolute top-full right-6 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white" />
        </div>
      )}
    </div>
  );
};

export default ContextWindowIndicator;
